import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, CreditCard, AlertCircle, Power, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PACKS = [
  { sku: "pack_50",   label: "50 Pages",    price: "$14.99" },
  { sku: "pack_250",  label: "250 Pages",   price: "$49.99", popular: true },
  { sku: "pack_1000", label: "1,000 Pages", price: "$129"  },
];

// Load Square Web Payments SDK once per page.
function useSquareSdk(environment) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (window.Square) { setLoaded(true); return; }
    const src = environment === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { existing.addEventListener("load", () => setLoaded(true)); return; }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => setLoaded(true);
    document.body.appendChild(s);
  }, [environment]);
  return loaded;
}

export default function AutoTopOffCard() {
  const [cfg, setCfg] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [sku, setSku] = useState("pack_250");
  const [threshold, setThreshold] = useState(90);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const cardRef = useRef(null);
  const containerRef = useRef(null);

  const sdkLoaded = useSquareSdk(cfg?.environment);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/payments/config`, { withCredentials: true }),
        axios.get(`${API}/payments/auto-topoff`, { withCredentials: true }),
      ]);
      setCfg(c.data);
      setSettings(s.data);
    } catch (e) {
      setError("Could not load settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Initialize Square card field when the form is open.
  useEffect(() => {
    if (!adding || !sdkLoaded || !cfg?.ready || !containerRef.current) return;
    let card;
    let cancelled = false;
    (async () => {
      try {
        const payments = window.Square.payments(cfg.application_id, cfg.location_id);
        card = await payments.card();
        if (cancelled) return;
        await card.attach(containerRef.current);
        cardRef.current = card;
      } catch (e) {
        setError(`Card field init failed: ${e?.message || e}`);
      }
    })();
    return () => {
      cancelled = true;
      try { card && card.destroy(); } catch {}
      cardRef.current = null;
    };
  }, [adding, sdkLoaded, cfg]);

  const enable = async () => {
    setError(""); setSuccess("");
    if (!cardRef.current) { setError("Card field not ready"); return; }
    try {
      const res = await cardRef.current.tokenize();
      if (res.status !== "OK") {
        setError(`Tokenize failed: ${res.errors?.[0]?.message || res.status}`);
        return;
      }
      const apiRes = await axios.post(`${API}/payments/auto-topoff/enable`, {
        source_id: res.token,
        package_sku: sku,
        threshold_pct: Number(threshold),
      }, { withCredentials: true });
      setSettings({ ...apiRes.data, configured: true });
      setAdding(false);
      setSuccess("Auto Top-Off enabled. We'll charge your saved card when you hit your threshold.");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not enable Auto Top-Off");
    }
  };

  const disable = async () => {
    if (!confirm("Disable Auto Top-Off? Your saved card will be kept; you can re-enable any time.")) return;
    try {
      await axios.post(`${API}/payments/auto-topoff/disable`, {}, { withCredentials: true });
      await load();
      setSuccess("Auto Top-Off disabled.");
    } catch (e) { setError("Disable failed"); }
  };

  if (loading) return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="auto-topoff-card">
      <div className="h-20 bg-slate-100 animate-pulse rounded" />
    </div>
  );

  if (!cfg?.ready) return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 opacity-70" data-testid="auto-topoff-card">
      <h2 className="font-bold text-slate-900 text-base mb-1" style={{ fontFamily: "Manrope" }}>Auto Top-Off</h2>
      <p className="text-sm text-slate-500">Square Application ID not yet configured. Contact support to enable this feature.</p>
    </div>
  );

  const isEnabled = settings?.enabled && settings?.configured;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="auto-topoff-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isEnabled ? "bg-emerald-100" : "bg-slate-100"}`}>
            <Zap className={`w-4 h-4 ${isEnabled ? "text-emerald-600" : "text-slate-400"}`} />
          </div>
          <div>
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Auto Top-Off</h2>
            <p className="text-xs text-slate-500">Automatically buy more pages when you hit your usage threshold.</p>
          </div>
        </div>
        {isEnabled && (
          <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full" data-testid="auto-topoff-status-enabled">
            ON
          </span>
        )}
      </div>

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="auto-topoff-success">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-900">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="auto-topoff-error">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-900">{error}</p>
        </div>
      )}

      {/* Saved card summary */}
      {isEnabled && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-3" data-testid="auto-topoff-saved-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">
                {settings.card_brand || "Card"} •••• {settings.card_last_4 || "????"}
              </span>
              {settings.card_exp_month && settings.card_exp_year && (
                <span className="text-xs text-slate-400">exp {settings.card_exp_month}/{String(settings.card_exp_year).slice(-2)}</span>
              )}
            </div>
            <button onClick={disable} data-testid="auto-topoff-disable-btn"
              className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1">
              <Power className="w-3 h-3" /> Disable
            </button>
          </div>
          <div className="mt-2 text-xs text-slate-600">
            Will charge for <strong>{settings.package_sku?.replace("pack_", "")} pages</strong> when you reach <strong>{settings.threshold_pct}%</strong> of your annual quota.
          </div>
        </div>
      )}

      {/* Enable form */}
      {!isEnabled && !adding && (
        <button onClick={() => { setAdding(true); setError(""); setSuccess(""); }}
          data-testid="auto-topoff-enable-btn"
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm py-2.5 rounded-lg flex items-center justify-center gap-2">
          <Zap className="w-4 h-4" /> Enable Auto Top-Off
        </button>
      )}

      {adding && (
        <div className="space-y-4" data-testid="auto-topoff-form">
          {/* Package selector */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2">Pack to auto-buy</label>
            <div className="grid grid-cols-3 gap-2">
              {PACKS.map(p => (
                <button key={p.sku} onClick={() => setSku(p.sku)} data-testid={`auto-topoff-pack-${p.sku}`}
                  className={`p-2 rounded-lg border-2 text-left transition-all ${sku === p.sku ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="text-xs font-bold text-slate-900">{p.label}</p>
                  <p className="text-[11px] text-slate-500">{p.price}</p>
                  {p.popular && <span className="inline-block mt-1 text-[9px] uppercase tracking-wider font-bold bg-purple-600 text-white px-1.5 py-0.5 rounded">Popular</span>}
                </button>
              ))}
            </div>
          </div>
          {/* Threshold */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2">Trigger when usage reaches</label>
            <div className="flex items-center gap-2">
              <input type="range" min={50} max={99} value={threshold} onChange={e => setThreshold(e.target.value)} className="flex-1" data-testid="auto-topoff-threshold" />
              <span className="text-sm font-bold text-slate-900 w-12 text-right">{threshold}%</span>
            </div>
          </div>
          {/* Square card field */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-2">Card details (PCI-secured by Square)</label>
            <div ref={containerRef} className="border border-slate-300 rounded-lg p-3 bg-white min-h-[60px]" data-testid="auto-topoff-card-field" />
            {!sdkLoaded && <p className="text-xs text-slate-400 italic mt-1">Loading Square card field…</p>}
          </div>
          {/* Actions */}
          <div className="flex gap-2">
            <button onClick={() => setAdding(false)} data-testid="auto-topoff-cancel-btn"
              className="flex-1 border border-slate-300 text-slate-700 font-semibold text-sm py-2 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={enable} data-testid="auto-topoff-save-btn"
              disabled={!sdkLoaded}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold text-sm py-2 rounded-lg flex items-center justify-center gap-2">
              <Zap className="w-4 h-4" /> Save Card & Enable
            </button>
          </div>
          <p className="text-[10px] text-slate-400 italic leading-relaxed">
            Card data is captured in a Square-hosted secure iframe and never touches our servers.
            You can disable Auto Top-Off any time. We'll email you before and after every auto-charge.
          </p>
        </div>
      )}
    </div>
  );
}
